import { useEffect, useRef, useState } from 'react';
import { healthApi } from '../services/api';
import { POLL_SCHEDULE, msUntilNextTick } from '../utils/pollSchedule';
import { setConnectionStatus } from '../services/connectionHealth';

// steady-state ping cadence while connected. #697: sourced from the shared poll
// schedule, which also fixes this poller's PHASE — see the scheduling note in `tick`.
const BASE_INTERVAL_MS = POLL_SCHEDULE.heartbeat.intervalMs;
const RETRY_START_MS = 3000; // first retry after a failure
const RETRY_MAX_MS = 30000; // backoff cap (doubles each consecutive failure)
const OFFLINE_AFTER_FAILURES = 3; // consecutive failures (while the browser still
// reports online) before escalating 'reconnecting' -> 'offline'

const defaultIsOnline = () => typeof navigator === 'undefined' || navigator.onLine !== false;

/**
 * Backend-connected heartbeat (#161): periodically pings the cheap, DB-free
 * `/health` endpoint (main.py `health_check` — see its own comment: "pinged
 * constantly, must stay cheap") and derives a connection status distinct from
 * the browser's `navigator.onLine`. #160 (useAutosave/offlineQueue) already
 * reacts to the browser's online/offline signal, but that only knows the
 * DEVICE's network interface is up — it says nothing about whether THIS
 * backend is actually reachable (a cold-starting Render free-tier dyno, a
 * backend-side outage, a proxy dropping requests). This hook is the
 * complementary, backend-aware signal that drives the status dot.
 *
 * Status: 'connected' | 'reconnecting' | 'offline'.
 * - 'connected': the last ping succeeded (optimistic before the first ping
 *   resolves, so the dot doesn't flash red on every page load).
 * - 'reconnecting': a ping just failed, browser still reports online, and the
 *   failure streak is still short — retrying with backoff.
 * - 'offline': the browser itself reports offline, OR `offlineAfterFailures`
 *   consecutive pings have failed while nominally online.
 *
 * Backs off on failure (`retryStartMs` doubling to `retryMaxMs`), and resets
 * straight back to the steady `baseIntervalMs` cadence on the next success.
 * Also listens for the browser's own `online`/`offline` events so a genuine
 * network drop/recovery is reflected immediately rather than waiting out the
 * current timer.
 */
export function useHeartbeat({
  pingFn = healthApi.ping,
  baseIntervalMs = BASE_INTERVAL_MS,
  retryStartMs = RETRY_START_MS,
  retryMaxMs = RETRY_MAX_MS,
  offlineAfterFailures = OFFLINE_AFTER_FAILURES,
  isOnline = defaultIsOnline,
} = {}) {
  const [status, setStatusState] = useState('connected');
  const timerRef = useRef(null);
  const failuresRef = useRef(0);
  const retryDelayRef = useRef(retryStartMs);
  // Extends the hook's ref-callback idiom (below) to `retryStartMs`. `tick`/`schedule`
  // are reassigned every render so they always see the latest option value, but the
  // mount-once effect's `online`/`offline` HANDLERS are registered once and would have
  // captured the mount-time number forever — the one place in this hook where a changed
  // option silently would not take effect. Mirroring it here makes the mount effect's
  // empty dependency array genuinely exhaustive instead of suppressed.
  const retryStartMsRef = useRef(retryStartMs);
  retryStartMsRef.current = retryStartMs;

  // Owner ask, 2026-07-28: "review feedback and notifications (and any other repeating
  // poll checks) should always be gated on health so that we don't just sit spamming a
  // broken server/connection."
  //
  // This hook already computes reachability; what it could not do is TELL anything that
  // is not a React component. `reviewActivityPoller` is a module singleton with no
  // component to read this hook's state from, so it polled a dead backend at full cadence
  // forever — and every one of those failures cost a CORS preflight as well. Publishing
  // each verdict into `services/connectionHealth` gives the non-React pollers the same
  // signal the status dot renders, and keeps the heartbeat as the SOLE writer of it (see
  // that module's design rule — two writers would deadlock recovery).
  //
  // EVERY status assignment must go through here. Assigning the React state directly
  // would leave the dot correct and silently un-gate every other poller, which is a
  // failure that only shows up in a network capture.
  const setStatus = (next) => {
    setStatusState(next);
    setConnectionStatus(next);
  };

  // Ref-callback idiom (same as hooks/useAutosave.js): `tick`/`schedule` are
  // reassigned on every render so they always close over the LATEST
  // pingFn/isOnline/etc, while the mount effect below registers timers/
  // listeners exactly once — a fresh function identity each render must not
  // re-arm the interval or re-subscribe the window listeners.
  const tickRef = useRef(null);
  const scheduleRef = useRef(null);

  scheduleRef.current = (delay) => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => tickRef.current(), delay);
  };

  // #697: schedule the STEADY-STATE ping on the shared wall-clock grid instead of
  // `now + baseIntervalMs`. A setTimeout chain armed from completion time drifts by
  // however long each request took, so this poller's phase wandered and could not be
  // kept off the notification or review-activity ticks by any amount of care at the
  // other end. Aligning it makes the separation a property of the schedule.
  //
  // BACKOFF stays deliberately OFF the grid: a failing backend is exactly when several
  // clients must not retry in lockstep, and the retry delays (3s doubling to 30s) are
  // about recovery, not cadence. Only the healthy path is aligned.
  const steadyDelay = () => (baseIntervalMs === POLL_SCHEDULE.heartbeat.intervalMs
    ? msUntilNextTick(baseIntervalMs, POLL_SCHEDULE.heartbeat.offsetMs)
    // A caller-overridden cadence (tests, and any future per-surface tuning) is not on
    // the shared grid by definition — fall back to the plain interval rather than
    // silently snapping it to a slot it does not belong to.
    : baseIntervalMs);

  // Hidden tabs don't ping (mirrors reviewActivityPoller/useNotifications's gate) — a
  // backgrounded tab has no one watching the status dot, and every OTHER poller already
  // stands down while hidden (#697), so there is nothing left that needs a live
  // connection-health signal during the hidden window. `onWake` in the mount effect
  // below pings immediately on becoming visible again, so the status catches up on the
  // very next look rather than waiting out up to a full `baseIntervalMs`.
  const isVisible = () => typeof document === 'undefined'
    || document.visibilityState !== 'hidden';

  tickRef.current = async () => {
    if (!isVisible()) {
      // Skip the network call entirely — do NOT touch failuresRef/retryDelayRef/status,
      // since this is neither a success nor a failure, just an unattended timer this
      // tab has no reason to run right now.
      scheduleRef.current(steadyDelay());
      return;
    }
    if (!isOnline()) {
      failuresRef.current = 0;
      retryDelayRef.current = retryStartMs;
      setStatus('offline');
      scheduleRef.current(retryStartMs);
      return;
    }
    try {
      await pingFn();
      failuresRef.current = 0;
      retryDelayRef.current = retryStartMs;
      setStatus('connected');
      scheduleRef.current(steadyDelay());
    } catch {
      failuresRef.current += 1;
      setStatus(failuresRef.current >= offlineAfterFailures ? 'offline' : 'reconnecting');
      // Schedule the NEXT retry at the current delay (retryStartMs the first
      // time), THEN double it for whichever failure comes after that — so the
      // first retry is prompt and only subsequent ones back off.
      scheduleRef.current(retryDelayRef.current);
      retryDelayRef.current = Math.min(retryDelayRef.current * 2, retryMaxMs);
    }
  };

  useEffect(() => {
    tickRef.current();

    const handleOnline = () => {
      // The browser just came back — reset the backoff and probe immediately
      // rather than waiting out whatever retry delay was in flight.
      retryDelayRef.current = retryStartMsRef.current;
      failuresRef.current = 0;
      tickRef.current();
    };
    const handleOffline = () => {
      failuresRef.current = 0;
      setStatus('offline');
      scheduleRef.current(retryStartMsRef.current);
    };
    // Becoming visible is exactly when a stale-while-hidden status is most likely to be
    // wrong, so ping immediately rather than waiting out the steady interval.
    const onWake = () => { if (isVisible()) tickRef.current(); };
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    document.addEventListener('visibilitychange', onWake);
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      document.removeEventListener('visibilitychange', onWake);
    };
    // Mount-once by design: the ref-callback idiom above means every option this
    // effect's handlers use is read through a ref at call time, so `[]` is genuinely
    // exhaustive — a fresh function identity per render must not re-arm the interval
    // or re-subscribe the window listeners.
  }, []);

  return { status };
}

export default useHeartbeat;
